import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { v4 as uuidv4 } from 'uuid';
import {
  ASK_USER_TOOL_NAME,
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type BaseDataMessage,
  type HITLDecision,
  type HITLRequestConfirmationMessage,
  type PromptOption,
  type TerraUIMessage,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  buildAskUserBranchResendMessage,
  createBranchMessageMetadata,
  toasterApi,
  type ChatRef,
} from '@terra/ui-design-system';
import { submitCopilotHitlDecision } from '../../utils/copilotSubagentHitl';
import { isRecoverableStreamError } from '../../utils/chatNetworkErrors';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { useChatTabsStore } from '../../store/chatTabsStore';
import { getQueuedMessageText } from './QueuedMessagesPanel';
import { useAddAlwaysAllowRule } from './useAddAlwaysAllowRule';
import type { useChatAttachmentsController } from './ChatAttachmentsController';
import type { useChatInstance } from '../../hooks/useChatInstance';

export function useCopilotChatEventHandlers({
  chatId,
  tabId,
  chatRef,
  chatComponentRef,
  editingQueuedMessageId,
  setEditingQueuedMessageId,
  appliedDraftInputRef,
  sentDraftTextRef,
  sendMessage,
  sendUserMessageWithResult,
  handleSend,
  sendQueuedMessageNow,
  requestBranchAskUserResend,
  streamError,
  isIntentionalAbort,
  resumeStream,
  regenerate,
  hasUploadingAttachments,
  peekPendingAttachments,
  clearPendingAttachments,
  restorePendingAttachments,
}: {
  chatId: string;
  tabId: string;
  chatRef?: React.RefObject<ChatRef | null>;
  chatComponentRef: React.RefObject<ChatRef | null>;
  editingQueuedMessageId: string | null;
  setEditingQueuedMessageId: (id: string | null) => void;
  appliedDraftInputRef: React.RefObject<string | null>;
  sentDraftTextRef: React.RefObject<string | null>;
  sendMessage: ReturnType<typeof useChatInstance>['sendMessage'];
  sendUserMessageWithResult: ReturnType<
    typeof useChatInstance
  >['sendUserMessageWithResult'];
  handleSend: ReturnType<typeof useChatInstance>['handleSend'];
  sendQueuedMessageNow: ReturnType<
    typeof useChatInstance
  >['sendQueuedMessageNow'];
  requestBranchAskUserResend: ReturnType<
    typeof useChatInstance
  >['requestBranchAskUserResend'];
  streamError: Error | undefined;
  isIntentionalAbort: () => boolean;
  resumeStream: () => Promise<void> | void;
  regenerate: () => void;
  hasUploadingAttachments: ReturnType<
    typeof useChatAttachmentsController
  >['hasUploadingAttachments'];
  peekPendingAttachments: ReturnType<
    typeof useChatAttachmentsController
  >['peekPendingAttachments'];
  clearPendingAttachments: ReturnType<
    typeof useChatAttachmentsController
  >['clearPendingAttachments'];
  restorePendingAttachments: ReturnType<
    typeof useChatAttachmentsController
  >['restorePendingAttachments'];
}) {
  const tCopilot = useTranslations('chat.copilot');
  const tFileUpload = useTranslations('designSystem.chat.fileUpload');
  const tHitlCard = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const { updateChatTab } = useChatTabsStore();

  const handleSendWithAttachments = async (message: TerraUIMessage) => {
    if (hasUploadingAttachments()) {
      toasterApi.error({
        title: tFileUpload('uploadError'),
        description: tFileUpload('waitForUploads'),
      });
      const textPart = message.parts?.find((part) => part.type === 'text');
      const textToRestore =
        textPart && typeof textPart.text === 'string'
          ? textPart.text
          : undefined;

      if (textToRestore) {
        queueMicrotask(() => {
          chatComponentRef.current?.setInput(textToRestore);
          chatComponentRef.current?.focus();
        });
      }

      return;
    }

    const sentText = message.parts?.find((part) => part.type === 'text');
    const sentDraftText =
      sentText && typeof sentText.text === 'string' ? sentText.text.trim() : '';

    if (sentDraftText) {
      sentDraftTextRef.current = sentDraftText;
      appliedDraftInputRef.current = sentDraftText;
      updateChatTab(tabId, { draftInput: undefined });
    }

    const attachmentsSnapshot = peekPendingAttachments();
    const parts = [...(message.parts ?? [])];

    if (attachmentsSnapshot.length > 0) {
      parts.push({
        type: TerraUIMessageDataPartName,
        data: {
          type: DataMessageTypeEnum.ConversationAttachments,
          attachments: attachmentsSnapshot.map((attachment) => ({
            id: attachment.id,
            fileName: attachment.fileName,
            fileType: attachment.fileType,
            sizeBytes: attachment.sizeBytes,
          })),
        },
      });
    }

    clearPendingAttachments();
    const sent = await handleSend({ ...message, parts });

    if (!sent) {
      if (sentDraftText) {
        sentDraftTextRef.current = null;
        appliedDraftInputRef.current = null;
        updateChatTab(tabId, { draftInput: sentDraftText });
        queueMicrotask(() => {
          chatComponentRef.current?.setInput(sentDraftText);
          chatComponentRef.current?.focus();
        });
      }

      if (attachmentsSnapshot.length > 0) {
        restorePendingAttachments(attachmentsSnapshot);
      }
    }
  };

  const handleDataMessageEvent = (event: {
    dataMessage: BaseDataMessage;
    selectedOption: PromptOption;
    message?: string;
  }) => {
    const { dataMessage, selectedOption, message } = event;

    const userInputText = message
      ? `${selectedOption?.value || ''}\n\nMessage: ${message}`
      : selectedOption?.value || '';

    const toolName =
      dataMessage.type === ConfirmationMessageTypeEnum.AskUser
        ? ASK_USER_TOOL_NAME
        : undefined;

    sendMessage({
      parts: [{ type: 'text', text: userInputText }],
      role: 'user',
      metadata: {
        subs: dataMessage.id ? [dataMessage.id] : [],
        response: selectedOption?.value || '',
        message: message || '',
        type: ConfirmationMessageTypeEnum.UserConfirmation,
        toolName,
      } as UserConfirmationMessage,
    }).catch((sendError: unknown) => {
      console.error('Generic confirmation sendMessage failed', sendError);
    });
  };

  const handleHITLDataMessageEvent = (event: {
    dataMessage: HITLRequestConfirmationMessage;
    selectedOption: PromptOption;
    decision: HITLRequestConfirmationMessage['decision'];
    decisions?: HITLDecision[];
  }): Promise<void> =>
    submitCopilotHitlDecision({
      chatId,
      dataMessage: event.dataMessage,
      selectedOption: event.selectedOption,
      decision: event.decision,
      decisions: event.decisions,
      sendMessage,
    }).catch((sendError: unknown) => {
      console.error('HITL confirmation failed', sendError);
      toasterApi.error({
        title: tHitlCard('actionFailedStatus', {
          message:
            sendError instanceof Error ? sendError.message : String(sendError),
        }),
      });

      throw sendError;
    });

  const handleRetry = () => {
    if (
      streamError &&
      isRecoverableStreamError(streamError) &&
      !isIntentionalAbort()
    ) {
      void Promise.resolve(resumeStream()).catch((resumeError: unknown) => {
        console.warn('Chat retry failed', resumeError);
      });
      chatRef?.current?.clearInput();

      return;
    }

    regenerate();
    chatRef?.current?.clearInput();
  };

  const handleAlwaysAllowRule = useAddAlwaysAllowRule();

  const handleContinueInterruptedTurn = useCallback(
    (continuePrompt: string) => {
      void handleSend({
        id: uuidv4(),
        role: 'user',
        parts: [{ type: 'text', text: continuePrompt }],
      });
    },
    [handleSend],
  );

  const handlePromptSuggestionClick = useCallback(
    (promptText: string) => {
      chatComponentRef.current?.setInput(promptText);
      chatComponentRef.current?.focus();
    },
    [chatComponentRef],
  );

  const startEditingQueuedMessage = useChatInstancesStore(
    (state) => state.startEditingQueuedMessage,
  );
  const cancelEditingQueuedMessage = useChatInstancesStore(
    (state) => state.cancelEditingQueuedMessage,
  );

  const handleSendQueuedMessageNow = useCallback(
    async ({ queueItemId }: { queueItemId: string }) => {
      try {
        await sendQueuedMessageNow({ queueItemId });
      } catch (error) {
        console.error('Failed to send queued message now:', error);
        toasterApi.error({
          title: tCopilot('queuedMessages.sendNowErrorTitle'),
          description: tCopilot('queuedMessages.sendNowErrorDescription'),
        });
      }
    },
    [sendQueuedMessageNow, tCopilot],
  );

  const handleEditQueuedMessage = useCallback(
    ({ queueItemId }: { queueItemId: string }) => {
      const queuedMessage = startEditingQueuedMessage({ chatId, queueItemId });

      if (!queuedMessage) {
        return;
      }

      chatComponentRef.current?.setInput(
        getQueuedMessageText({ queuedMessage }),
      );
      chatComponentRef.current?.focus();
      setEditingQueuedMessageId(queueItemId);
    },
    [
      chatComponentRef,
      chatId,
      setEditingQueuedMessageId,
      startEditingQueuedMessage,
    ],
  );

  useEffect(() => {
    if (!editingQueuedMessageId) {
      return;
    }

    return () => {
      cancelEditingQueuedMessage({
        chatId,
        queueItemId: editingQueuedMessageId,
      });
    };
  }, [cancelEditingQueuedMessage, chatId, editingQueuedMessageId]);

  const handleBranchAskUserDataMessageEvent = useCallback(
    async (event: {
      dataMessage: BaseDataMessage;
      selectedOption: PromptOption;
    }) => {
      await requestBranchAskUserResend(
        async ({
          branchFromMessageId,
          branchEditSource: activeBranchEditSource,
          selectedOption,
        }) => {
          if (activeBranchEditSource.kind !== 'ask-user') {
            return false;
          }

          const payload = buildAskUserBranchResendMessage({
            selectedOption,
            askUserDataMessage: activeBranchEditSource.askUserDataMessage,
          });

          try {
            return await sendUserMessageWithResult({
              id: uuidv4(),
              role: 'user',
              parts: payload.parts,
              metadata: {
                ...(payload.metadata as UserConfirmationMessage),
                ...createBranchMessageMetadata({ branchFromMessageId }),
              },
            });
          } catch (error) {
            console.error('Error sending ask-user branch resend:', error);

            return false;
          }
        },
        event.selectedOption,
      );
    },
    [requestBranchAskUserResend, sendUserMessageWithResult],
  );

  return {
    handleSendWithAttachments,
    handleDataMessageEvent,
    handleHITLDataMessageEvent,
    handleRetry,
    handleAlwaysAllowRule,
    handleContinueInterruptedTurn,
    handlePromptSuggestionClick,
    handleSendQueuedMessageNow,
    handleEditQueuedMessage,
    handleBranchAskUserDataMessageEvent,
  };
}
